// Gameplay client API dla world-chunks endpointu (Faza 2A).
// Endpoint: GET /api/world/chunk/:x/:y — zwraca chunk + tile_objects + biome + templates.
// Auth: session token (httpOnly cookie + Authorization header z client).

import { apiGet, apiRequest } from "./client";

// Mirror response shape z apps/server/src/features/world-chunks/routes.ts.
// Lokalne typy — nie wynosimy do shared-types dopóki contract jest unstable (Faza 2C edge regen może rozszerzyć).

export interface WorldChunkPublic {
  id: string;
  chunkX: number;
  chunkY: number;
  biomeCode: string;
}

export interface WorldBiomePublic {
  code: string;
  namePl: string;
  backgroundColor: string;
}

export interface WorldTileObjectPublic {
  id: string;
  positionX: number;
  positionY: number;
  templateCode: string;
}

export interface WorldTileTemplatePublic {
  code: string;
  namePl: string;
  category: string;
  isWalkable: number;
  isInteractive: number;
  spritePath: string;
}

export interface WorldChunkResponse {
  chunk: WorldChunkPublic;
  biome: WorldBiomePublic | null;
  objects: WorldTileObjectPublic[];
  templates: WorldTileTemplatePublic[];
}

/** Wyrzuca błąd przy 404 (brak chunka lub DRAFT). Caller decyduje co pokazać graczowi. */
export async function fetchChunk(
  chunkX: number,
  chunkY: number,
): Promise<WorldChunkResponse> {
  return apiGet<WorldChunkResponse>(`/api/world/chunk/${chunkX}/${chunkY}`);
}

// === INTERACT (Faza 2B) ===

export interface InteractDrop {
  itemTemplateCode: string;
  namePl: string;
  count: number;
}

export type InteractResult =
  | {
      ok: true;
      drops: InteractDrop[];
      cooldownUntil: string;
      newStamina: number;
    }
  | {
      ok: false;
      code: string;
      error: string;
      cooldownUntil?: string;
    };

/** POST /api/world/interact — klik na sąsiedni interactive obiekt → drop loot lub miss. */
export async function interactTile(input: {
  chunkX: number;
  chunkY: number;
  positionX: number;
  positionY: number;
}): Promise<InteractResult> {
  try {
    return await apiRequest<InteractResult>({
      method: "POST",
      path: "/api/world/interact",
      body: input,
    });
  } catch (err) {
    // ApiError 422 zwraca payload jako body → wyciągamy
    if (
      err &&
      typeof err === "object" &&
      "status" in err &&
      (err as { status: number }).status === 422 &&
      "body" in err
    ) {
      const body = (err as { body: unknown }).body;
      if (body && typeof body === "object" && "ok" in body && (body as { ok: boolean }).ok === false) {
        return body as InteractResult;
      }
    }
    throw err;
  }
}

// === Helpery convert global ↔ chunk coords ===
// player.currentPositionX/Y to GLOBAL coords. Chunk = floor(pos/24), local = pos%24.

export const CHUNK_SIZE = 24;

export function globalToChunkCoord(globalCoord: number): number {
  return Math.floor(globalCoord / CHUNK_SIZE);
}

export function globalToLocalCoord(globalCoord: number): number {
  // Modulo z handling dla ujemnych (JS % zwraca negatyw dla negatywnych)
  const m = globalCoord % CHUNK_SIZE;
  return m < 0 ? m + CHUNK_SIZE : m;
}
